/**
 * 4.35 PRAL — rescale nutrients AND carry their certificate (web mirror of the app's
 * utils/pralTransfer.ts). Every path that rebuilds numbers goes through here so a
 * certificate follows them only when it was valid for the source (transferPralSig).
 */
import type { Nutrients, StoredFoodItem } from '../types'
import { scaleNutrients, sumNutrients } from './nutrients'
import { PRAL_KEYS, isEntryBreakdownCoherent, transferPralSig } from '../kidney/pral'

export function scaleCertified(
  nutrients: Nutrients,
  pralSig: string | null | undefined,
  factor: number,
): { nutrients: Nutrients; pralSig?: string } {
  const scaled = scaleNutrients(nutrients, factor)
  const sig = transferPralSig(nutrients, pralSig, scaled)
  return sig ? { nutrients: scaled, pralSig: sig } : { nutrients: scaled }
}

/**
 * Per-serving numbers for a favorite made from a diary entry (mobile perServingFromEntry).
 * Everything comes from the entry's own totals ÷ servings — those carry the user's
 * corrections. ONLY the five PRAL inputs are taken from a coherent food breakdown instead
 * (foods are per serving): dividing the rounded consumed totals drifts until the re-logged
 * entry reads as incoherent and loses its PRAL.
 */
export function perServingFromEntry(entry: {
  nutrients: Nutrients
  pralSig?: string | null
  servingsConsumed?: number
  foods?: StoredFoodItem[]
}): { nutrients: Nutrients; pralSig?: string } {
  const servings = entry.servingsConsumed || 1
  const nutrients = scaleNutrients(entry.nutrients, 1 / servings)
  if (entry.foods?.length && isEntryBreakdownCoherent(entry)) {
    const fromFoods = scaleNutrients(sumNutrients(entry.foods.map((f) => f.nutrients)), 1)
    for (const k of PRAL_KEYS) {
      if (nutrients[k] != null && fromFoods[k] != null) nutrients[k] = fromFoods[k] as number
    }
  }
  const sig = transferPralSig(entry.nutrients, entry.pralSig, nutrients)
  return sig ? { nutrients, pralSig: sig } : { nutrients }
}

/** Plain water: every PRAL input is a known zero. */
export const WATER_NUTRIENTS = {
  sodiumMg: 0,
  potassiumMg: 0,
  phosphorusMg: 0,
  proteinG: 0,
  magnesiumMg: 0,
  calciumMg: 0,
} as const
